import { MailboxInfoInput } from 'owa-graph-schema';
import { getApolloClient } from 'owa-apollo';
import {
    ConversationRowsDocument,
    ConversationRowsQuery,
} from './graphql/__generated__/ConversationRowsQuery.interface';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import SortBy from 'owa-mail-list-store/lib/store/schema/SortBy';
import ViewFilter from 'owa-service/lib/contract/ViewFilter';
import {
    getGqlSortColumnFromOwsSortColumn,
    getGqlViewFilterFromOwsViewFilter,
    getGqlFocusedViewFilterFromOwsFocusedViewFilter,
} from 'owa-mail-list-store';
import { getInboxPausedDateTimeAsString } from './utils/getInboxPausedDateTimeAsString';
import folderIdToName from 'owa-session-store/lib/utils/folderIdToName';
import { mapFindConversationResponseMessageToGql } from 'owa-mail-conversation-row-gql-mappers';
import { isFeatureEnabled } from 'owa-feature-flags';
import { RequestOptions } from 'owa-service/src/RequestOptions';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import {
    tryConsumeConversationRows,
    tryConsumeFindConversationResponse,
} from 'owa-session-data-stash';

type ConversationRowConnection = ConversationRowsQuery['conversationRows'];

export async function getConversationRows(
    folderId: string,
    numberOfRowsToFetch: number,
    viewFilter: ViewFilter,
    sortBy: SortBy,
    canFolderSortByRenewTime: boolean,
    focusedViewFilter: FocusedViewFilter,
    shapeName: string,
    mailboxInfoInput: MailboxInfoInput,
    lastInstanceKey?: string,
    categoryName?: string,
    searchFolderId?: string,
    requestOptions?: RequestOptions,
    shouldTryToUseSessionDataResponse: boolean = false
): Promise<ConversationRowConnection> {
    // If this is a request for data at the start of the list,
    // and we've been asked to, try using the session data.
    if (shouldTryToUseSessionDataResponse && !lastInstanceKey) {
        // In native host, the gql stash is from hx so fall back to the legacy stash if
        // mon-conv-useHxForConvListViewLoad is not set.
        if (
            isHostAppFeatureEnabled('nativeResolvers') &&
            !isFeatureEnabled('mon-conv-useHxForConvListViewLoad')
        ) {
            const sessionDataFindConversationResponse = tryConsumeFindConversationResponse(
                folderId,
                0
            );

            if (sessionDataFindConversationResponse) {
                // If available, return the session data response mapped to the GQL schema
                return Promise.resolve(
                    mapFindConversationResponseMessageToGql(sessionDataFindConversationResponse)
                );
            }
        } else {
            const sessionDataConversationRows = await tryConsumeConversationRows(folderId, 0);

            if (sessionDataConversationRows) {
                return Promise.resolve(sessionDataConversationRows);
            }
        }
    }

    const client = await getApolloClient();
    const result = await client.query({
        query: ConversationRowsDocument,
        variables: {
            first: numberOfRowsToFetch,
            after: lastInstanceKey,
            folderId: folderId,
            searchFolderId: searchFolderId,
            viewFilter: getGqlViewFilterFromOwsViewFilter(viewFilter),
            focusedViewFilter: getGqlFocusedViewFilterFromOwsFocusedViewFilter(focusedViewFilter),
            sortBy: {
                sortColumn: getGqlSortColumnFromOwsSortColumn(sortBy.sortColumn),
                sortDirection: sortBy.sortDirection,
                canSortByRenewTime: canFolderSortByRenewTime,
            },
            category: categoryName,
            mailboxInfo: mailboxInfoInput,
            shapeName: shapeName,
            isScheduledFolder: folderIdToName(folderId) == 'scheduled',
            pausedInboxTime: getInboxPausedDateTimeAsString(folderId),
        },
        context: {
            requestOptions: requestOptions,
        },
    });

    return result.data.conversationRows;
}
